import { ActiveCallHost } from '@/features/calls/components/ActiveCallHost';
import { IncomingCallHost } from '@/features/calls/popups/IncomingCallHost';
import { isDesktopCallWindow } from '@/features/desktop/callWindow';
import { PresenceRuntime } from '@/features/presence/PresenceRuntime';
import { NotificationRuntime } from './NotificationRuntime';
import { MessagePopupStack } from './components/MessagePopupStack';
import { useDemoStream } from './hooks/useDemoStream';

/**
 * A DESKTOP CALL WINDOW IS NOT A SECOND WORKSPACE.
 *
 * It loads the same app from the same origin, so without this it would mount
 * the same always-on machinery a second time, in a second document — and every
 * one of those is a duplicate with teeth:
 *
 *   NotificationRuntime  a second gateway subscription to the notification
 *                        feed, so every mention raises TWO OS banners, and a
 *                        second `useBadgeSync` fighting the first over the
 *                        taskbar badge count.
 *   IncomingCallHost     a second ringing card for the same invite, a second
 *                        ringtone playing over it, and — worse — a second
 *                        `useTransferredCall`, which would handle the same
 *                        `call.transferred` event twice in two documents.
 *   MessagePopupStack    message toasts stacked over a video call.
 *   PresenceRuntime      a second heartbeat and a second idle monitor for one
 *                        person, disagreeing about whether they are away.
 *
 * What the call window DOES need is the call itself, so `ActiveCallHost` is the
 * one thing that survives here. `isDesktopCallWindow()` is fixed for the life
 * of the document (the shell stamps it into argv at creation), so branching on
 * it before any hook runs is stable.
 */
export function AppOverlays() {
  if (isDesktopCallWindow()) return <ActiveCallHost />;
  return <WorkspaceOverlays />;
}

/**
 * Every always-on overlay in one fixed column, anchored BOTTOM RIGHT.
 *
 * That is where Teams puts them, and the reason is worth keeping: the top of
 * the window is chrome you read deliberately — search, your account, the
 * conversation you chose — while the bottom corner is where something is
 * allowed to interrupt you. Anchored to the top, a popup landed ON the title
 * bar and covered the very controls it was competing with.
 *
 * Order matters. The stack grows UPWARDS from the corner, so the LAST child sits
 * closest to it: the incoming call, which is the one thing here that expires if
 * you miss it. Messages pile above it and can wait.
 *
 * One fixed container = one compositor layer for the whole popup system. The
 * transient toast stack was moved to the opposite corner (packages/ui/Toast) so
 * the two can never overlap. `pointer-events` are off on the column itself so
 * the app underneath stays fully clickable between cards.
 */
function WorkspaceOverlays() {
  useDemoStream();

  return (
    <>
      <PresenceRuntime />
      <NotificationRuntime />

      {/* The live call. Above the router, so leaving `/call/:id` shrinks it to
          a corner tile rather than hanging up — and OUTSIDE the popup column
          below, which is anchored bottom-right and would otherwise stack a
          ringing call on top of the call already in progress.

          In the DESKTOP shell this renders nothing: the call lives in a window
          of its own and `ActiveCallHost` refuses to mount a second copy of it
          here. See features/desktop/callWindow.ts. */}
      <ActiveCallHost />

      <div className="pointer-events-none fixed bottom-5 right-5 z-popup flex max-h-[calc(100vh-2.5rem)] flex-col items-end justify-end gap-3 overflow-hidden">
        <div className="pointer-events-auto">
          <MessagePopupStack />
        </div>
        <div className="pointer-events-auto">
          <IncomingCallHost />
        </div>
      </div>
    </>
  );
}
